<template>
    <div>
        <div v-if="clickable">
            <v-avatar :tile="tile" :size="size" v-bind="avatar" @click="click" style="cursor: pointer">
                <img v-if="url && !status.loading" :src="url" />

                <slot v-if="!url && !status.loading" name="none">
                    <v-icon large>account_circle</v-icon>
                </slot>

                <slot v-if="status.loading" name="loading" :status="status">
                    <v-progress-circular :value="status.progress" :size="32" :width="2" color="primary" />
                </slot>
            </v-avatar>

            <input
                type="file"
                ref="file"
                name="file"
                :accept="accept"
                @change="select($event.target.name, $event.target.files)"
                style="display: none"
            />
        </div>

        <div v-else>
            <v-avatar v-bind="avatar">
                <img v-if="url" :src="url" />

                <slot v-else name="none">
                    <v-icon large>account_circle</v-icon>
                </slot>
            </v-avatar>
        </div>
    </div>
</template>

<script>
import { error } from "@/controllers/alert";

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/gif,image/svg+xml";
const EXTENSION_BY_MIME = {
    "image/jpeg": ["jpg", "jpeg"],
    "image/png": ["png"],
    "image/gif": ["gif"],
    "image/svg+xml": ["svg"],
};

export default {
    props: {
        url: {
            type: String,
            required: true,
        },
        tile: {
            type: Boolean,
            default: false,
        },
        request: {
            type: Function,
            required: true,
        },
        rename: {
            type: Function,
            required: false,
            default: (file) => file.name,
        },
        field: {
            type: String,
            required: false,
            default: "file",
        },
        clickable: {
            type: Boolean,
            required: false,
            default: true,
        },
        /** Max file size in kilobytes (matches API max:2048). */
        maxSize: {
            type: Number,
            required: false,
            default: 2048,
        },
        /** Native file picker filter; align with backend mimes where possible. */
        accept: {
            type: String,
            required: false,
            default: DEFAULT_ACCEPT,
        },
        headers: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        avatar: {
            type: Object,
            required: false,
            default: () => ({}),
        },
        size: {
            type: Number,
            required: false,
            default: 150,
        },
    },

    data: () => ({
        status: {
            loading: false,
            progress: 0,
        },
    }),

    computed: {
        maxSizeMB() {
            return Math.round((this.maxSize / 1024) * 100) / 100;
        },
        allowedMimeTypes() {
            return this.accept
                .split(",")
                .map((item) => item.trim().toLowerCase())
                .filter(Boolean);
        },
        allowedExtensions() {
            const exts = new Set();
            this.allowedMimeTypes.forEach((mime) => {
                if (mime === "image/*") {
                    Object.values(EXTENSION_BY_MIME)
                        .flat()
                        .forEach((ext) => exts.add(ext));
                    return;
                }
                (EXTENSION_BY_MIME[mime] || []).forEach((ext) => exts.add(ext));
            });
            return exts;
        },
    },

    methods: {
        click() {
            if (!this.status.loading) {
                if (!this.url) {
                    this.choose();
                } else {
                    this.replace();
                }
            } else {
                this.$emit("cancel");
            }
        },

        choose() {
            return this.$refs.file.click();
        },

        replace() {
            if (this.$listeners.replace) {
                this.$emit("replace");
            } else {
                this.choose();
            }
        },

        resetFileInput() {
            if (this.$refs.file) {
                this.$refs.file.value = "";
            }
        },

        isValidImage(file) {
            const mime = (file.type || "").toLowerCase();
            const ext = (file.name.split(".").pop() || "").toLowerCase();

            if (this.allowedMimeTypes.includes("image/*")) {
                if (mime.startsWith("image/")) {
                    return !ext || this.allowedExtensions.has(ext);
                }
                return this.allowedExtensions.has(ext);
            }

            if (mime && this.allowedMimeTypes.includes(mime)) {
                return true;
            }

            if (!mime && ext && this.allowedExtensions.has(ext)) {
                return true;
            }

            return false;
        },

        async showInvalidTypeAlert() {
            this.$emit("error-type");
            await error(this, "invalid_image_type");
        },

        async showFileTooLargeAlert() {
            this.$emit("error-size");
            await this.$swal.fire({
                icon: "error",
                title: this.$t("error"),
                text: this.$t("file_too_large", { maxSizeMB: this.maxSizeMB }),
                showConfirmButton: true,
                confirmButtonColor: "#438EF2",
            });
        },

        async select(field, fileList) {
            const [image] = fileList;

            if (!fileList.length) {
                this.$emit("error-empty");
                return;
            }

            const sizeKb = image.size / 1024;

            if (!this.isValidImage(image)) {
                this.resetFileInput();
                await this.showInvalidTypeAlert();
                return;
            }

            if (sizeKb > this.maxSize) {
                this.resetFileInput();
                await this.showFileTooLargeAlert();
                return;
            }

            this.upload(image);
        },

        async upload(file) {
            this.status.loading = true;

            const form = new FormData();

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                    ...this.headers,
                },

                onUploadProgress: function (event) {
                    if (event.lengthComputable) {
                        const progress = (event.loaded / event.total) * 100;

                        this.status.progress = progress;

                        this.$emit("progress", progress);
                    }
                }.bind(this),
            };

            form.append(this.field, file, this.rename(file));

            try {
                const upload = await this.request(form, config);

                this.$emit("emitFile", file);
                this.$emit("success", upload);
            } catch (e) {
                this.$emit("failed", e);
            } finally {
                this.status.loading = false;
                this.status.progress = 0;
                this.resetFileInput();
            }
        },
    },
};
</script>
